import React, { useCallback, useEffect, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import {
  GOOGLE_CALENDAR_API_KEY,
  GOOGLE_CALENDAR_ID,
} from "config/google-calendar";
import dayGridPlugin from "@fullcalendar/daygrid";
import axios from "axios";
import moment from "moment";
import styles from "./Calendar.module.scss";
import { ReactComponent as ArrowLeftIcon } from "assets/images/ArrowLeft.svg";
import { ReactComponent as ArrowRightIcon } from "assets/images/ArrowRight.svg";
import clsx from "clsx";
import "./Calendar.scss";
import { useDeviceSize, useStateValues } from "providers";
import { getTimezoneName } from "utils";

const Calendar = ({ isOpened = false, eventTypeIds = [], isStatic = false }) => {
  const deviceSize = useDeviceSize();
  const {
    action: { setSelectedEvents, setIsOpened, setSelectedDate },
  } = useStateValues();
  const [date, setDate] = useState(moment().startOf("month"));
  const [timeMin, setTimeMin] = useState(null);
  const [timeMax, setTimeMax] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [fcEvents, setFCEvents] = useState([]);
  const fullCalendarRef = useRef(null);

  useEffect(() => {
    const _timeMin = date
      .clone()
      .startOf("year")
      .format("YYYY-MM-DDT00:00:00.000[Z]");
    const _timeMax = date
      .clone()
      .add(1, "years")
      .startOf("year")
      .format("YYYY-MM-DDT00:00:00.000[Z]");

    if (_timeMin !== timeMin && _timeMax !== timeMax) {
      setTimeMin(_timeMin);
      setTimeMax(_timeMax);
    }
    // eslint-disable-next-line
  }, [date]);

  // eslint-disable-next-line
  useEffect(() => fetchEvents(), [timeMin, timeMax]);

  useEffect(() => {
    setFCEvents(() => {
      const events = [];

      allEvents.forEach((event) => {
        for (const eventTypeId of eventTypeIds) {
          if (event.filter.includes(eventTypeId)) {
            events.push(event);
            break;
          }
        }
      });

      return events;
    });
  }, [allEvents, eventTypeIds]);

  const fetchEvents = useCallback(() => {
    if (!timeMin || !timeMax) return;

    axios
      .get(
        `https://www.googleapis.com/calendar/v3/calendars/${GOOGLE_CALENDAR_ID}/events`,
        {
          params: {
            key: GOOGLE_CALENDAR_API_KEY,
            timeMin,
            timeMax,
            singleEvents: true,
            maxResults: 9999,
          },
        }
      )
      .then((response) => {
        const events = [];

        try {
          const divTag = document.createElement("div");
          const zoneName = getTimezoneName();
          const date = new Date();
          const timezone = -date.getTimezoneOffset();

          for (const event of response.data.items) {
            let startDate, endDate;
            if (
              (!event.start.date || !event.end.date) &&
              (!event.start.dateTime || !event.end.dateTime)
            )
              continue;
            if (event.start.date) {
              startDate = event.start.date + " 00:00:00Z";
              endDate = event.end.date + " 00:00:00Z";
            } else {
              startDate = event.start.dateTime;
              endDate = event.end.dateTime;
            }

            const description = event.description
              .replace("<html-blob>", "")
              .replace("</html-blob>", "")
              .split("||EVENT_INFO||")[0];
            divTag.innerHTML = event.description;
            const eventData = divTag.textContent.split("||EVENT_INFO||");
            eventData.shift();

            events.push({
              title: event.summary,
              start: startDate,
              end: endDate,
              description,
              timezone,
              zoneName,
              ...JSON.parse(eventData),
            });
          }
        } catch (error) {}

        setAllEvents(events);
        window.__RT_EVENTS_LOADED = true;
      });
  }, [timeMin, timeMax]);

  const handleClickPrevMonth = (e) => {
    e.preventDefault();

    fullCalendarRef.current.getApi().prev();
    setDate(moment(fullCalendarRef.current.getApi().getDate()));
  };

  const handleClickNextMonth = (e) => {
    e.preventDefault();

    fullCalendarRef.current.getApi().next();
    setDate(moment(fullCalendarRef.current.getApi().getDate()));
  };

  const handleClickToday = (e) => {
    e.preventDefault();

    fullCalendarRef.current.getApi().gotoDate(new Date());
    setDate(moment(fullCalendarRef.current.getApi().getDate()));
  };

  const renderDayCellContent = ({ date, dayNumberText, isOther }) => {
    const allEvents = fcEvents.filter(({ condense, start, end }) => {
      if (condense) return moment(date).isSame(start, "day");

      if (moment(end).diff(start, "days") > 0)
        return (
          moment(date).isBetween(start, end) ||
          moment(date).isSame(start, "day")
        );

      return moment(date).isSame(start, "day");
    });

    const normalEvents = [],
      cornerEvents = [];
    allEvents.forEach((event) => {
      if (event.top_left_corner_image) cornerEvents.push(event);
      else normalEvents.push(event);
    });

    const handleEvents = () => {
      if (isOther) return;

      const events = fcEvents.filter(({ start, end }) => {
        if (moment(end).diff(start, "days") > 0) {
          return (
            moment(date).isBetween(start, end) ||
            moment(date).isSame(start, "day")
          );
        }
        return moment(date).isSame(start, "day");
      });

      if (events.length > 0) {
        setSelectedEvents(events);
        setSelectedDate(moment(date).format("dddd, MMMM Do"));
        setIsOpened(true);
      } else {
        setIsOpened(false);
        setTimeout(() => setSelectedEvents(events), 400);
      }
    };

    const size =
      normalEvents.length > 2
        ? "small"
        : normalEvents.length > 1
        ? "small--2"
        : "big";
    const isMobile = !["xxl", "xl", "sxl", "lg", "md"].includes(deviceSize);

    return (
      <div
        className={clsx(
          styles.calendar__day__cell,
          styles[`calendar__day__cell--${size}`]
        )}
        onClick={isStatic ? undefined : handleEvents}
      >
        <div className="fc-daygrid-day-border" />
        <div className="fc-daygrid-day-border-clone" />

        <span
          className={styles.calendar__day__number}
          dangerouslySetInnerHTML={{ __html: dayNumberText }}
        />

        <div className={styles.calendar__day__event__wrapper}>
          {normalEvents.map((event, eIdx) =>
            eIdx > 2 ? null : (
              <div
                key={eIdx}
                className={clsx(
                  styles.calendar__day__event,
                  styles[`calendar__day__event--${size}`],
                  isMobile &&
                    normalEvents.length > 3 &&
                    styles[`calendar__day__event--small--more`],
                  isMobile && normalEvents.length > 3
                    ? styles[
                        `calendar__day__event--small--more__date--${dayNumberText}`
                      ]
                    : styles[
                        `calendar__day__event--${size}__date--${dayNumberText}`
                      ],
                  "rt-calendar-event"
                )}
              >
                <img
                  className={clsx(
                    styles.calendar__day__event__img,
                    styles[`calendar__day__event__img--${size}`]
                  )}
                  src={
                    normalEvents.length > 1
                      ? event.small_image
                      : event.large_image
                  }
                  alt="rt-event"
                />
              </div>
            )
          )}
        </div>

        {normalEvents.length > 3 && (
          <button
            className={clsx(
              styles.button__more_events,
              styles[`button__more_events--${dayNumberText}`]
            )}
            dangerouslySetInnerHTML={{ __html: "And More!" }}
          />
        )}

        {!!cornerEvents.length && (
          <img
            className={clsx(
              styles.calendar__day__event__corner,
              "rt-calendar-event--corner"
            )}
            src={cornerEvents[0].top_left_corner_image}
            alt="Meta"
          />
        )}
      </div>
    );
  };

  return (
    <div
      className={clsx(styles.wrapper, isOpened && styles["wrapper--opened"])}
    >
      <div className={styles.toolbar}>
        <div className={styles.toolbar__left}>
          <img
            src={`/assets/images/update_images/${date.format("MM")}.png`}
            alt={date.format("MMMM")}
          />
        </div>

        {!isStatic && (
          <div className={styles.toolbar__right}>
            <button
              className={styles.button__prev__month}
              onClick={handleClickPrevMonth}
            >
              <ArrowLeftIcon />
            </button>

            <button
              className={styles.button__today}
              onClick={handleClickToday}
              dangerouslySetInnerHTML={{ __html: "Today" }}
            />

            <button
              className={styles.button__next__month}
              onClick={handleClickNextMonth}
            >
              <ArrowRightIcon />
            </button>
          </div>
        )}
      </div>

      <div className={clsx(styles.calendar, "rt-calendar")}>
        <FullCalendar
          ref={fullCalendarRef}
          plugins={[dayGridPlugin]}
          initialView="dayGridMonth"
          headerToolbar={null}
          fixedWeekCount={false}
          height="auto"
          firstDay={1}
          dayHeaderClassNames={styles.calendar__day__header}
          dayHeaderContent={({ text }) =>
            `${text === "Tue" ? "Tues" : text === "Thu" ? "Thurs" : text}.`
          }
          dayCellContent={renderDayCellContent}
        />
      </div>
    </div>
  );
};

export default Calendar;
